/**
 * Reserve cart items — what the client posts to `POST /api/cart/reserve` to
 * place a soft-hold on listing stock immediately before launching the
 * checkout flow. The server delegates the stock + FK check to the
 * `reserve_items_atomic` RPC; the boundary parse only verifies envelope shape.
 *
 * Mobile-consumed (api-kraahl.ts reserveCartStock — sends `{ listingId,
 * quantity }`). The route handler then maps `listingId` → `listing_id` for
 * the RPC; we keep the wire field name `listingId` for backwards compat with
 * the frozen mobile clients.
 *
 * @see app/api/cart/reserve/route.ts — handler
 * @see apps/mobile/lib/api-kraahl.ts reserveCartStock — mobile caller
 * @see .tondocs/payments/API_REQUEST_BOUNDARY_AUDIT.md H-8
 */

import { z } from "zod";
import { pinned } from "./pin";

export interface CartReserveItem {
  /** `listings.id` of the item to reserve. */
  listingId: string;
  /** Units to reserve. Positive integer. */
  quantity: number;
}

export interface CartReserveRequest {
  /** Non-empty array of items to reserve. Server rejects empty arrays. */
  items: CartReserveItem[];
}

const CartReserveItemSchema = z.object({
  listingId: z.string().min(1),
  quantity: z.number().int().positive(),
});

export const CartReserveRequestSchema = pinned<CartReserveRequest>()(
  z.object({
    items: z.array(CartReserveItemSchema).min(1),
  }),
);
